'use client';

import { c as _c } from "react/compiler-runtime";
import type * as React from 'react';
import { AnimatedProgressCircle } from '@/components/ui/animated-progress-circle';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { Panel, PanelContent, PanelHeader, PanelTitle } from '@/components/ui/panel';
import { InfoTipModal, type TipData } from './info-tip';
import { PointsChip } from './points-chip';
interface EarnCardProps {
  title: string;
  /** Opens a how-it-works {@link InfoModal} from the header's `ⓘ` button. */
  tip?: TipData;
  /** Extra header affordance rendered after the title + tip (e.g. a "Clips ↗" link). */
  titleAdornment?: React.ReactNode;
  /** Points pill (e.g. "+75 pts") shown on the right of the description row. */
  points?: string;
  /** Optional glyph rendered before the points pill label. */
  pointsIcon?: IconSvgElement;
  description?: string;
  /** Completion 0–100 → a green progress ring in the header's top-right. */
  progress?: number;
  /** Count rendered inside the ring (e.g. "2/5"). */
  progressLabel?: string;
  children?: React.ReactNode;
  'data-testid'?: string;
}

/**
 * Pear-style "earn card" shell, matched to the profile-page card header (MOR-771):
 * a plain title + info glyph on the left and a progress ring on the right, with
 * the description and points pill sharing one row below (MOR-774). Consumers
 * (referrals / streak / post / clip) pass their own action row as `children`.
 */
export function EarnCard(t0) {
  const $ = _c(25);
  const {
    title,
    tip,
    titleAdornment,
    points,
    pointsIcon,
    description,
    progress,
    progressLabel,
    children,
    "data-testid": dataTestId
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <span className="truncate">{title}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tip) {
    t2 = tip !== undefined ? <InfoTipModal tip={tip} /> : null;
    $[2] = tip;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== titleAdornment) {
    t3 = <span className="flex min-w-0 items-center gap-1.5">{t1}{t2}{titleAdornment}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = titleAdornment;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== progress || $[9] !== progressLabel) {
    t4 = progress !== undefined ? <span className="flex shrink-0 items-center gap-1.5">{progressLabel !== undefined ? <span className="text-caption font-medium tabular-nums text-foreground-secondary">{progressLabel}</span> : null}<AnimatedProgressCircle progress={progress} diameter={20} strokeWidth={4} className="shrink-0" /></span> : null;
    $[8] = progress;
    $[9] = progressLabel;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <PanelHeader><PanelTitle className="flex items-center justify-between gap-2 text-body-lg">{t3}{t4}</PanelTitle></PanelHeader>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== description || $[15] !== points || $[16] !== pointsIcon) {
    t6 = description !== undefined || points !== undefined ? <div className="flex items-center justify-between gap-2">{description !== undefined ? <p className="min-w-0 text-caption text-foreground-secondary">{description}</p> : <span aria-hidden={true} />}{points !== undefined ? <PointsChip className="gap-1">{pointsIcon !== undefined ? <Icon icon={pointsIcon} className="size-icon-xs" aria-hidden={true} /> : null}{points}</PointsChip> : null}</div> : null;
    $[14] = description;
    $[15] = points;
    $[16] = pointsIcon;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = children !== undefined ? children : null;
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <PanelContent className="flex flex-col gap-3">{t6}{t7}</PanelContent>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== dataTestId || $[22] !== t5 || $[23] !== t8) {
    t9 = <Panel density="compact" data-testid={dataTestId}>{t5}{t8}</Panel>;
    $[21] = dataTestId;
    $[22] = t5;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}